import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Typography, TextField, Button, Table, TableHead, TableRow,
  TableCell, TableBody, Paper, TableFooter, Chip,
  FormControl, InputLabel, MenuItem, Select,
} from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getTrialBalance } from "./accountingApi";
import { useFinancialYear } from "./useFinancialYear";
import { AccountLedgerDialog, VoucherDialog } from "./LedgerDrilldown";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const TYPE_COLOR = { ASSET: "primary", LIABILITY: "warning", EQUITY: "success", INCOME: "info", EXPENSE: "error" };

export default function TrialBalance() {
  const [from, setFrom]           = useState("");
  const [to, setTo]               = useState("");
  const [branchCode, setBranch]   = useState("");

  useFinancialYear(setFrom, setTo);
  const [branches, setBranches]   = useState([]);
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  // Drill-down: the account whose ledger is open, and the voucher opened from it. The ledger uses
  // the range and branch the Trial Balance was generated with, not whatever is typed in since.
  const [generated, setGenerated] = useState(null);
  const [drillAccount, setDrillAccount] = useState(null);
  const [voucherId, setVoucherId] = useState(null);

  const tenancyId = localStorage.getItem("tenancyId");
  const token     = localStorage.getItem("jwtToken");

  const allowedBranches = useMemo(() => {
    try {
      const raw = localStorage.getItem("allowedBranches");
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch { return []; }
  }, []);

  useEffect(() => {
    fetch(`/api/${tenancyId}/branches`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const list = Array.isArray(data) ? data : data.branches || [];
        const filtered = allowedBranches.length
          ? list.filter((b) => allowedBranches.includes(b.branchCode))
          : list;
        setBranches(filtered);
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const load = async () => {
    if (!from || !to) return;
    setLoading(true);
    const data = await getTrialBalance(from, to, branchCode);
    setRows(Array.isArray(data) ? data : []);
    setGenerated({ from, to, branchCode });
    setLoading(false);
  };

  // Ledger amounts carry 4 decimals (GST splits like 47.6190). Adding them as floating point
  // leaves tiny errors, so a balanced ledger could compare unequal; sum in 1/10000 units instead.
  const toUnits = (n) => Math.round(Number(n || 0) * 10000);
  const drUnits = rows.reduce((s, r) => s + toUnits(r.debit), 0);
  const crUnits = rows.reduce((s, r) => s + toUnits(r.credit), 0);
  const totalDr = drUnits / 10000;
  const totalCr = crUnits / 10000;
  const balanced = drUnits === crUnits;

  const exportXlsx = () => {
    const ws = XLSX.utils.json_to_sheet(rows.map((r) => ({
      "Account Code": r.accountCode, "Account Name": r.accountName,
      "Type": r.accountType, "Debit": r.debit, "Credit": r.credit, "Net Balance": r.netBalance,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Trial Balance");
    saveAs(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })]), `TrialBalance_${from}_${to}.xlsx`);
  };

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>Trial Balance</Typography>
      {rows.length > 0 && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          Click an account to see its entries, then a voucher to see the bill behind it.
        </Typography>
      )}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
          <TextField label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel>Branch</InputLabel>
            <Select value={branchCode} label="Branch" onChange={(e) => setBranch(e.target.value)}>
              <MenuItem value="">All Branches</MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.branchCode} value={b.branchCode}>
                  {b.branchCode} - {b.branchName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={load} disabled={loading}>Generate</Button>
          {rows.length > 0 && <Button variant="outlined" onClick={exportXlsx}>Export Excel</Button>}
        </Box>
      </Paper>

      {rows.length > 0 && (
        <Table size="small" component={Paper}>
          <TableHead sx={{ bgcolor: "primary.dark" }}>
            <TableRow>
              <TableCell sx={{ color: "white" }}>Code</TableCell>
              <TableCell sx={{ color: "white" }}>Account Name</TableCell>
              <TableCell sx={{ color: "white" }}>Type</TableCell>
              <TableCell align="right" sx={{ color: "white" }}>Debit</TableCell>
              <TableCell align="right" sx={{ color: "white" }}>Credit</TableCell>
              <TableCell align="right" sx={{ color: "white" }}>Net Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow
                key={i}
                hover
                onClick={() => r.ledgerAccountId && setDrillAccount(r)}
                sx={{ cursor: r.ledgerAccountId ? "pointer" : "default" }}
              >
                <TableCell>{r.accountCode}</TableCell>
                <TableCell>{r.accountName}</TableCell>
                <TableCell>
                  <Chip label={r.accountType} size="small" color={TYPE_COLOR[r.accountType] || "default"} />
                </TableCell>
                <TableCell align="right">{r.debit > 0 ? fmt(r.debit) : ""}</TableCell>
                <TableCell align="right">{r.credit > 0 ? fmt(r.credit) : ""}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmt(r.netBalance)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableCell colSpan={3}><b>Total</b></TableCell>
              <TableCell align="right"><b>{fmt(totalDr)}</b></TableCell>
              <TableCell align="right"><b>{fmt(totalCr)}</b></TableCell>
              <TableCell align="right">
                <Chip label={balanced ? "✓ Balanced" : `✗ Off by ${fmt(Math.abs(totalDr - totalCr))}`} color={balanced ? "success" : "error"} size="small" />
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      )}

      {generated && (
        <>
          <AccountLedgerDialog
            account={drillAccount}
            from={generated.from}
            to={generated.to}
            branchCode={generated.branchCode}
            onClose={() => setDrillAccount(null)}
            onOpenVoucher={setVoucherId}
          />
          <VoucherDialog
            voucherHeaderId={voucherId}
            onClose={() => setVoucherId(null)}
            onOpenAccount={(acc) => { setVoucherId(null); setDrillAccount(acc); }}
          />
        </>
      )}
    </Box>
  );
}
